import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AdminDTO } from 'src/app/models/admin-dto.model';
import { ApplicantInfoDTO } from 'src/app/models/applicant-info-dto.model';
import { CertificationPersonDTO } from 'src/app/models/certification-person-dto';
import { CourseApprovalLocationAndContacts } from 'src/app/models/course-approval-location.model';
import { FieldAssessmentCheckListModel } from 'src/app/models/field-assessment-checklist.model';
import { ProgramReviewAssesments } from 'src/app/models/program-review-assessments-model';
import { TrainingProgramAttestationSection } from 'src/app/models/tp-attestation-section.model';
import { TrainingProgramApplicationProcessInfoModel } from 'src/app/models/training-program-application-process-info.model';
import { TrainingProgramApplicationInfoModel } from 'src/app/models/training-program-application.model';
import { TrainingProgramReqEnteredData } from 'src/app/models/training-program-req-entered-data.model';
import { TrainingProgramReqSection } from 'src/app/models/training-program-req-section.model';
import { environment } from 'src/environments/environment';
import { TrainingProgramServiceInterface } from './training-program.interface';
import { HasPendingChangeResponseModel } from 'src/app/models/has-pending-change-response.model';
import { ProgramStatForTp } from 'src/app/models/program-stats-for-tp';

@Injectable({
  providedIn: 'root'
})
export class TrainingProgramService implements TrainingProgramServiceInterface {

  public reload = new BehaviorSubject<boolean>(false);
  constructor(private httpClient: HttpClient) {
  }

  GetAllTrainingPrograms(isFrench: boolean, hasPrerequisites: boolean = false): Observable<any[]> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `TrainingProgram/${isFrench}/${hasPrerequisites}`);
  }

  ValidateProgramId(programId: string, trainingProgramId: string, trainingTypeId: string, isFrench: boolean): Observable<TrainingProgramApplicationInfoModel> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `TrainingProgram/ValidateProgramId/${programId}/${trainingProgramId}/${trainingTypeId}/${isFrench}`);
  }

  SaveTrainingProgramApplication(tpInfo: TrainingProgramApplicationInfoModel) {
    return this.httpClient.post<any>(environment.apiBaseUrl + 'TrainingProgram/SaveTrainingProgramApplication', tpInfo);
  }

  GetCourseApprovalDTOById(courseApprovalId: string | null): Observable<TrainingProgramApplicationInfoModel> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `TrainingProgram/GetCourseApprovalDTOById/${courseApprovalId}`);
  }

  GetTPAByCourseAprovalId(courseApprovalId: string, version: number): Observable<TrainingProgramAttestationSection[]> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `TrainingProgram/GetTPAByCourseApprovalId/${courseApprovalId}/${version}`);
  }

  SaveTrainingProviderAttestation(tpAttestation: TrainingProgramAttestationSection[], courseApprovalId: string, validateDto: boolean = true): Observable<any> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `TrainingProgram/SaveTrainingProviderAttestation/${courseApprovalId}?validateDto=` + validateDto, tpAttestation);
  }

  SaveTrainingProgramCourseApprovalInformation(courseApprovalLocation: CourseApprovalLocationAndContacts, validateDto: boolean = true) {
    return this.httpClient.post<any>(environment.apiBaseUrl + 'TrainingProgram/SaveTrainingProgramCourseApprovalInformation?validateDto=' + validateDto, courseApprovalLocation);
  }

  SaveTrainingProgramLatestPageIndex(courseApprovalId: string, latestPageIndex: number) {
    return this.httpClient.get<any>(environment.apiBaseUrl + 'TrainingProgram/SaveTrainingProgramLatestPageIndex/' + courseApprovalId + '/' + latestPageIndex);
  }

  CheckDuplicateApplication(isFrench: boolean, trainingProgramId: string, trainingTypeId: string): Observable<boolean> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `TrainingProgram/CheckDuplicateApplication/${isFrench}/${trainingProgramId}/${trainingTypeId}`);
  }

  GetTrainingProgramApplicationsByTrainingProviderId(showDependents?: boolean): Observable<TrainingProgramApplicationProcessInfoModel[]> {
    return this.httpClient.get<any>(environment.apiBaseUrl + 'TrainingProgram/MyTrainingPrograms/' + (showDependents ?? false));
  }

  UpdateCourseApprovalSubmissionDate(courseApprovalId: string | null) {
    return this.httpClient.get<boolean>(environment.apiBaseUrl + `TrainingProgram/UpdateCourseApprovalSubmissionDate/${courseApprovalId}`);
  }

  DeleteCourseApproval(courseApprovalId: string) {
    return this.httpClient.delete<boolean>(environment.apiBaseUrl + `TrainingProgram/DeleteCourseApproval/${courseApprovalId}`);
  }

  GetProgramDetailsDTOById(courseApprovalId: string | null): Observable<TrainingProgramApplicationProcessInfoModel> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `TrainingProgram/GetProgramDetailsDTOById/${courseApprovalId}`);
  }

  GetTrainingProgramRequirementsAccordionInfoByCourseApprovalIdAndPageName(courseApprovalId: string, pageName: string, version: number): Observable<TrainingProgramReqSection[]> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `TrainingProgram/GetTrainingProgramRequirementsAccordions/${courseApprovalId}/${pageName}/${version}`);
  }

  SaveTrainingProgramRequirements(data: TrainingProgramReqEnteredData[], courseApprovalId: string, validateDto: boolean = true): Observable<any> {
    return this.httpClient.post<any>(environment.apiBaseUrl + 'TrainingProgram/SaveTrainingProgramRequirements/' + courseApprovalId + '?validateDto=' + validateDto, data);
  }

  ValiateTrainingProgramRequirements(courseApprovalId: string, data: TrainingProgramReqEnteredData): Observable<TrainingProgramReqEnteredData> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `TrainingProgram/ValiateTrainingProgramRequirements/${courseApprovalId}`, data);
  }

  WithdrawCourseApproval(data: any): Observable<boolean> {
    return this.httpClient.post<any>(environment.apiBaseUrl + 'TrainingProgram/WithdrawCourseApproval/', data);
  }

  CancelApprovedCourseWithdrawalRequest(data: any): Observable<boolean> {
    return this.httpClient.post<any>(environment.apiBaseUrl + 'TrainingProgram/CancelApprovedCourseWithdrawalRequest/', data);
  }

  GetDeficienciesCountsForTabs(courseApprovalId: string): Observable<ProgramReviewAssesments[]> {
    return this.httpClient.get<ProgramReviewAssesments[]>(environment.apiBaseUrl + `TrainingProgram/GetProgramReviewAssessments/${courseApprovalId}`);
  }

  GetCourseApprovalAssessorInfo(courseApprovalId: string): Observable<AdminDTO> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `trainingProgram/GetCourseApprovalAssessorInfo/${courseApprovalId}`);
  }

  GetFieldAssessmentChecklist(courseApprovalId: string, isFrench: boolean, version: number): Observable<Array<FieldAssessmentCheckListModel>> {
    return this.httpClient.get<Array<FieldAssessmentCheckListModel>>(environment.apiBaseUrl + `trainingProgram/GetFieldAssessmentChecklist/${courseApprovalId}/${isFrench}/${version}`);
  }

  SaveFieldAssessmentChecklist(fieldAssessmentChecklists: Array<FieldAssessmentCheckListModel>): Observable<boolean> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `trainingProgram/SaveFieldAssessmentChecklist`, fieldAssessmentChecklists);
  }

  GetSubmittedByForPublicPortal(courseApprovalId: string): Observable<CertificationPersonDTO> {
    return this.httpClient.get<CertificationPersonDTO>(environment.apiBaseUrl + `trainingProgram/GetSubmittedByForPublicPortal/${courseApprovalId}`);
  }

  GetApplicationInfo(courseApprovalId: string): Observable<ApplicantInfoDTO> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `trainingProgram/GetApplicationInfo/${courseApprovalId}`);
  }

  UpdateCourseApprovalBannerDisplayFlag(courseApprovalId: string | null) {
    return this.httpClient.get<boolean>(environment.apiBaseUrl + `TrainingProgram/UpdateCourseApprovalBannerDisplayFlag/${courseApprovalId}`);
  }
  UpdateStatusPagevisitFlag(courseApprovalId: string | null,statusID: number, version: number) {
    return this.httpClient.get<boolean>(environment.apiBaseUrl + `TrainingProgram/UpdateStatusPagevisitFlag/${courseApprovalId}/${statusID}/${version}`);
  }

  GetCoursApprovalNextChangeRequestVersion(courseApprovalId: string) {
    return this.httpClient.get<number>(environment.apiBaseUrl + `TrainingProgram/GetCoursApprovalNextChangeRequestVersion/${courseApprovalId}`);
  }

  SubmitProactiveChangeEvent(courseApprovalId: string, hasAttestationAnswerChanged: boolean) {
    return this.httpClient.get<boolean>(environment.apiBaseUrl + `TrainingProgram/SubmitProactiveChangeEvent/${courseApprovalId}/${hasAttestationAnswerChanged}`);
  }

  CancelProactiveChange(courseApprovalId: string) {
    return this.httpClient.get<boolean>(environment.apiBaseUrl + `TrainingProgram/CancelProactiveChange/${courseApprovalId}`);
  } 

  HasPendingChangeRequest(courseApprovalId: string | null) {
    return this.httpClient.get<HasPendingChangeResponseModel>(environment.apiBaseUrl + `TrainingProgram/HasPendingChangeRequest/${courseApprovalId}`);
  }

  GetProgramStats(courseApprovalId: string): Observable<ProgramStatForTp> {
    return this.httpClient.get<ProgramStatForTp>(environment.apiBaseUrl + `trainingProgram/GetProgramStatsByCourseApprovalIdForTp/${courseApprovalId}`);
  }

}
